import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { EnvironmentInstanceToken, PaceEnvironment } from '../../interfaces/environment.interface';
import { TimeRecordingEntryModel } from '../../models/complex/time-recording-entry.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { TimesheetEntryModel } from '../../models/complex/timesheet-entry.model';
import { TimesheetGapModel } from '../../models/complex/timesheet-gap.model';
import { TimesheetManualEntryModel } from '../../models/complex/timesheet-manual-entry.model';
import { TimesheetSowModel } from '../../models/complex/timesheet-sow.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { DateModel } from '../../models/primitive/date.model';
import { TimeModel } from '../../models/primitive/time.model';
import { ConfigService } from '../config/config.service';
import { TimesheetAndExpensesService } from './timesheet-and-expenses.service';
import { WorkScheduleService } from './work-schedule.service';

export class TimesheetNormalEditableEntriesValidationResult {
    public constructor(
        public isInvalid = false,
        public notificationId = new NotificationIdentifierModel('', '', ''), // notification for which the entry is invalid
        public validationErrorMessage = '',
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class TimesheetService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly configService: ConfigService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly workScheduleService: WorkScheduleService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    /**
     * Returns if all manual entries in the provided list start on the same date as provided.
     *
     * Background: Before we can sort the entries, close the gaps and update start/end date and time, we need to make
     *             sure that the entry is actually valid. Otherwise there are scenarios where an invalid timesheet could
     *             be constructed.
     *
     * @param entries All the entries from the timesheet
     * @returns True if all manual entries start on the same date as the SoW entry
     * @throws Error if no SoW entry is found
     */
    public checkManualStartDatesAa(entries: TimesheetEntryInterface[]): boolean {
        const sowEntry = entries.find((e) => e instanceof TimesheetSowModel);

        if (!sowEntry) {
            throw new Error('Could not find start of work entry in list (which for NGMAA should never happen)');
        }

        const manualEntries = entries.filter((e) => e instanceof TimesheetManualEntryModel);

        return _.every(manualEntries, (e) => e.startDate.value.isSame(sowEntry.startDate.value, 'days'));
    }

    /**
     * Sort the entries in the timesheet according to their start times
     *
     * @param entries All the entries from the timesheet
     * @returns The sorted list of entries
     * @throws Error if client is not recognized
     */
    public sortEntries(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        if (this.clientManager.isNgmP58()) {
            return this.sortEntriesForP58(entries);
        } else if (this.clientManager.isNgmAP0()) {
            return this.sortEntriesForNgmAa(entries);
        } else {
            throw new Error(
                `TimesheetService: sortEntries: Don't know how to proceed for app id ${this.environment.appId}`,
            );
        }
    }

    public sortEntriesForP58(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        // store SoW and EoW if they are already set
        const sow = _.first(entries);
        if (sow && TimesheetAndExpensesService.isSowEntry(sow)) {
            entries.shift();
        }
        const eow = _.last(entries);
        if (eow && TimesheetAndExpensesService.isEowEntry(eow)) {
            entries.pop();
        }
        // sort only the notification and manual entries, as the gap entries are added via closeGaps method.
        entries = entries.filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry));
        const sortedEntries = TimesheetModel.sortTimesheetEntries(entries);

        if (sow && TimesheetAndExpensesService.isSowEntry(sow)) {
            sortedEntries.unshift(sow);
        }
        if (eow && TimesheetAndExpensesService.isEowEntry(eow)) {
            sortedEntries.push(eow);
        }

        return sortedEntries;
    }

    public sortEntriesForNgmAa(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        // For ANZ and China, we display an optional additional travel back entry, which should be always at last.
        // So, we remove it while sorting and add it back after finishing sorting of other entries.
        let additionalTravelBackEntry: TimesheetEntryInterface | undefined;
        if (
            this.configService.getDisplayAdditionalTravelBack() &&
            TimesheetAndExpensesService.isManualEntry(entries[entries.length - 1])
        ) {
            additionalTravelBackEntry = entries.pop();
        }

        // For NGM AA, we can have time slots outside of SoW and EoW
        // there could be the case where a manual entry is outside the workschedule (after EoW)
        // at that moment eow entry startDate and manual entry startDate values might be same
        // for which endDate check is included.
        const sortedEntries = entries.sort((entry1: TimesheetEntryInterface, entry2: TimesheetEntryInterface) => {
            if (entry1.startDate.value.isBefore(entry2.startDate.value)) {
                return -1;
            } else if (entry1.startDate.value.isSame(entry2.startDate.value)) {
                if (entry1.endDate.value.isBefore(entry2.endDate.value)) {
                    return -1;
                } else if (entry1.endDate.value.isSame(entry2.endDate.value)) {
                    // on days without work schedule, SoW and EoW are set to the same date and time (0:00).
                    // While sorting we have to make sure that SoW will always remain before EoW, hence the additional checks.
                    if (TimesheetAndExpensesService.isSowEntry(entry1)) {
                        return -1;
                    } else if (TimesheetAndExpensesService.isSowEntry(entry2)) {
                        return 1;
                    } else {
                        return 0;
                    }
                } else {
                    return 1;
                }
            } else {
                return 1;
            }
        });

        if (additionalTravelBackEntry) {
            sortedEntries.push(additionalTravelBackEntry);
        }

        return sortedEntries;
    }

    public closeGaps(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        if (this.clientManager.isNgmP58()) {
            return this.closeGapsForP58(entries);
        } else if (this.clientManager.isNgmAP0()) {
            return this.closeGapsForNgmAa(entries);
        } else {
            throw new Error(
                `TimesheetService: closeGaps: Don't know how to proceed for app id ${this.environment.appId}`,
            );
        }
    }

    /**
     * Check for gaps between existing timesheet entries for NGM P58
     * and create a new gap model to fill them
     *
     * @param {TimesheetEntryInterface[]} entries the list of timesheet entries
     * @returns {TimesheetEntryInterface[]} the list of timesheet entries with gaps closed
     */
    private closeGapsForP58(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        const fixedList: TimesheetEntryInterface[] = [];
        let previousEntry: TimesheetEntryInterface | undefined;

        const minimumNumberOfEntries = 2;

        // gaps can only happen between at least two entries
        if (entries.length < minimumNumberOfEntries) {
            return entries;
        }

        entries
            .filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry))
            .forEach((entry) => {
                // check if there is a gap
                if (previousEntry && entry.startTime.value.diff(previousEntry.endTime.value) > 0) {
                    fixedList.push(
                        new TimesheetGapModel(previousEntry.endTime.value.clone(), entry.startTime.value.clone()),
                    );
                }

                // add entry to fixed list
                fixedList.push(entry);

                // update previous entry
                previousEntry = entry;
            });

        this.updateStartAndEndLimits(fixedList);

        return fixedList;
    }

    /**
     * Check for gaps between existing timesheet entries for workschedule defined days
     * and create a new gap model to fill them
     *
     * @param entries the list of timesheet entries
     * @returns the list of timesheet entries with gaps closed
     */
    public closeGapsForNgmAa(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        // For NGMAA, there are always entries: Either SoW and EoW are created due to the work schedule logic or
        // we already have gap entries from previous runs of this gap closing function
        const date = entries[0].startTime.value.clone().startOf('day');
        const workSchedule = this.workScheduleService.getWorkScheduleByDate(date);

        const displayNonProductiveHours = this.configService.getDisplayNonProductiveHours();
        const displayAdditionalTravelBack = this.configService.getDisplayAdditionalTravelBack();

        const gaplessList: TimesheetEntryInterface[] = [];

        let entryIsMandatory = false;

        let previousEntry: TimesheetEntryInterface;

        const entriesWithoutGaps = entries.filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry));

        // For SA, we do not have any workschedule, but we need to add mandatory gap entries.
        if (this.clientManager.isNgmSA()) {
            previousEntry = new TimesheetGapModel(date.clone(), date.clone());

            // Remove gap entries from the list before closing the gaps. SoW and EoW will be added later if necessary.
            entriesWithoutGaps
                .filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry))
                .forEach((entry) => {
                    if (
                        TimesheetAndExpensesService.isSowEntry(entry) ||
                        TimesheetAndExpensesService.isEowEntry(entry)
                    ) {
                        gaplessList.push(entry);
                        return;
                    }

                    if (!entryIsMandatory) {
                        const gapEntries = gaplessList.filter(
                            (gapEntry) =>
                                TimesheetAndExpensesService.isGapEntry(gapEntry) ||
                                TimesheetAndExpensesService.isManualEntry(gapEntry),
                        );
                        entryIsMandatory = gapEntries.length > 0;
                    }

                    // if there is a gap between the previous entry and the current entry, we need to close the gap by creating a gap entry
                    // the entry is optional if it is the first one
                    if (entry.startTime.value.isAfter(previousEntry.endTime.value) && displayNonProductiveHours) {
                        gaplessList.push(
                            new TimesheetGapModel(
                                previousEntry.endTime.value.clone(),
                                entry.startTime.value.clone(),
                                entryIsMandatory,
                            ),
                        );
                    }

                    // push the entry itself
                    gaplessList.push(entry);

                    previousEntry = entry;
                });
        } else {
            // Check if the list has a gap between midnight and the first entry. If yes, push an optional entry to close it
            if (entriesWithoutGaps[0].startTime.value.isAfter(date) && displayNonProductiveHours) {
                previousEntry = new TimesheetGapModel(
                    date.clone(),
                    entriesWithoutGaps[0].startTime.value.clone(),
                    false,
                );
                gaplessList.push(previousEntry);
            } else {
                previousEntry = new TimesheetGapModel(date.clone(), date.clone());
            }

            // Remove gap entries from the list before closing the gaps. SoW and EoW will be added later if necessary.
            entriesWithoutGaps
                .filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry))
                .forEach((entry) => {
                    // SoW is taken care of by the manually created entry before the loop
                    if (TimesheetAndExpensesService.isSowEntry(entry)) {
                        gaplessList.push(entry);
                        return;
                    }

                    // EoW is essentially being ignored - we need the entry to split mandatory/optional gaps, but it should not influence
                    // the start time of successive entries
                    if (TimesheetAndExpensesService.isEowEntry(entry)) {
                        gaplessList.push(entry);
                        return;
                    }

                    // set the mandatory flag based on the previous entry end time is in between the defined workschedule
                    entryIsMandatory =
                        previousEntry.endTime.value.isSameOrAfter(workSchedule.startOfWork) &&
                        previousEntry.endTime.value.isBefore(workSchedule.endOfWork);

                    // if there is a gap between the previous entry and the current entry, we need to close the gap by creating a gap entry
                    // the entry is optional if it is the first one
                    if (entry.startTime.value.isAfter(previousEntry.endTime.value) && displayNonProductiveHours) {
                        gaplessList.push(
                            new TimesheetGapModel(
                                previousEntry.endTime.value.clone(),
                                entry.startTime.value.clone(),
                                entryIsMandatory,
                            ),
                        );
                    }

                    // push the entry itself
                    gaplessList.push(entry);

                    previousEntry = entry;
                });

            // the list can have two "Add time slot" buttons at the end - one if there is work schedule left to cover, the other one
            // outside of the work schedule until the end of the day.

            // Let's first check if there is still work schedule left to cover
            if (previousEntry.endTime.value.isBefore(workSchedule.endOfWork) && displayNonProductiveHours) {
                const entry = new TimesheetGapModel(
                    previousEntry.endTime.value.clone(),
                    workSchedule.endOfWork.clone(),
                    true,
                );
                gaplessList.push(entry);
                previousEntry = entry;
            }
        }
        // Now check if there is time left between the last entry and the end of the day, which we will cover with an optional
        // gap entry
        if (
            previousEntry.endTime.value.isBefore(workSchedule.endOfWork.clone().endOf('day').startOf('minute')) &&
            displayNonProductiveHours
        ) {
            gaplessList.push(
                new TimesheetGapModel(
                    previousEntry.endTime.value.clone(),
                    workSchedule.endOfWork.clone().endOf('day').startOf('minute'),
                    false,
                ),
            );
        }

        const gaplessListEntriesWithGaps = gaplessList.filter((entry) => TimesheetAndExpensesService.isGapEntry(entry));

        const gaplessListNormalEntries = gaplessList.filter((entry) =>
            TimesheetAndExpensesService.isNormalEntry(entry),
        );

        // if the display additional travel back is true then
        // add a new timesheet gap model which can be seen as "Add Travel Back" button at the end
        if (
            displayAdditionalTravelBack &&
            gaplessListNormalEntries.length > 0 &&
            gaplessListEntriesWithGaps.length === 0 &&
            gaplessList[gaplessList.length - 1] instanceof TimesheetEntryModel
        ) {
            // if the last entry is a normal entry only then add a gap
            gaplessList.push(new TimesheetGapModel(date.clone(), date.clone(), false));
        }

        this.updateStartAndEndLimitsAa(gaplessList);

        return gaplessList;
    }

    private getTimesheetForNextDay(entry: TimesheetEntryInterface): TimesheetEntryInterface | undefined {
        const nextDay = entry.startTime.value.clone().add(1, 'day').startOf('day');
        const timesheet = this.timesheetAndExpensesService.getTimesheetForDate(nextDay);
        if (timesheet) {
            const timesheetEntries = timesheet.getTimesheetEntries();
            return timesheetEntries ? timesheetEntries[0] : undefined;
        }
        return undefined;
    }

    /**
     * Takes a gap-less list of entries and updates the minValue for the start time and the max value for the end time
     * so that no overlap is allowed to be entered by the user
     *
     * @param {TimesheetEntryInterface[]} entries the list of timesheet entries
     */
    private updateStartAndEndLimitsAa(entries: TimesheetEntryInterface[]): void {
        let previousEntry: TimesheetEntryInterface | undefined;

        const filteredEntries = entries.filter(
            (entry) =>
                !TimesheetAndExpensesService.isGapEntry(entry) &&
                !TimesheetAndExpensesService.isSowEntry(entry) &&
                !TimesheetAndExpensesService.isEowEntry(entry),
        );

        // no need to update any limits; also accesses further down would fail.
        if (filteredEntries.length === 0) {
            return;
        }

        // For NGMAA, SoW and EoW are set by work schedule, but should not directly influence the time limits - eg. travel back
        // can extend beyond EoW
        filteredEntries.forEach((entry) => {
            if (previousEntry) {
                const maxValueTime = entry.startTime.value.toObject();
                const minValueTime = previousEntry.endTime.value.toObject();

                const previousEntryStartDateTime = previousEntry.startDate.value;
                const previousEntryEndDateTime = previousEntry.endDate.value;

                const previousEntryStartDateOptions = previousEntry.startDate.options;
                previousEntryStartDateOptions.maxValue.set(maxValueTime);
                previousEntry.startDate = <DateModel>(
                    previousEntry.set(
                        new DateModel(
                            previousEntry.startDate.name,
                            previousEntryStartDateTime,
                            previousEntryStartDateOptions,
                        ),
                    )
                );
                const previousEntryStartTimeOptions = previousEntry.startTime.options;
                previousEntryStartTimeOptions.maxValue.set(maxValueTime);
                previousEntry.startTime = <TimeModel>(
                    previousEntry.set(
                        new TimeModel(
                            previousEntry.startTime.name,
                            previousEntryStartDateTime,
                            previousEntryStartTimeOptions,
                        ),
                    )
                );
                const previousEntryEndDateOptions = previousEntry.endDate.options;
                previousEntryEndDateOptions.maxValue.set(maxValueTime);
                previousEntry.endDate = <DateModel>(
                    previousEntry.set(
                        new DateModel(
                            previousEntry.endDate.name,
                            previousEntryEndDateTime,
                            previousEntryEndDateOptions,
                        ),
                    )
                );
                const previousEntryEndTimeOptions = previousEntry.endTime.options;
                previousEntryEndTimeOptions.maxValue.set(maxValueTime);
                previousEntry.endTime = <TimeModel>(
                    previousEntry.set(
                        new TimeModel(
                            previousEntry.endTime.name,
                            previousEntryEndDateTime,
                            previousEntryEndTimeOptions,
                        ),
                    )
                );

                const entryStartDateTime = entry.startDate.value;
                const entryEndDateTime = entry.endDate.value;

                const entryEndDateOptions = entry.endDate.options;
                entryEndDateOptions.minValue.set(minValueTime);
                entry.endDate = <DateModel>(
                    entry.set(new DateModel(entry.endDate.name, entryEndDateTime, entryEndDateOptions))
                );
                const entryEndTimeOptions = entry.endTime.options;
                entryEndTimeOptions.minValue.set(minValueTime);
                entry.endTime = <TimeModel>(
                    entry.set(new TimeModel(entry.endTime.name, entryEndDateTime, entryEndTimeOptions))
                );

                const entryStartDateOptions = entry.startDate.options;
                entryStartDateOptions.minValue.set(minValueTime);
                entry.startDate = <DateModel>(
                    entry.set(new DateModel(entry.startDate.name, entryStartDateTime, entryStartDateOptions))
                );
                const entryStartTimeOptions = entry.startTime.options;
                entryStartTimeOptions.minValue.set(minValueTime);
                entry.startTime = <TimeModel>(
                    entry.set(new TimeModel(entry.startTime.name, entryStartDateTime, entryStartTimeOptions))
                );
            }

            // update previous entry
            previousEntry = entry;
        });

        // update limits of first and last entry to allow covering the time from midnight to SoW and from EoW to midnight
        const firstEntry = filteredEntries[0];
        const lastEntry = filteredEntries[filteredEntries.length - 1];

        const firstEntryStartDateTime = firstEntry.startDate.value;
        const lastEntryStartDateTime = lastEntry.startDate.value;
        const lastEntryEndDateTime = lastEntry.endDate.value;
        const lastEntryStartDateOptions = lastEntry.startDate.options;
        const lastEntryStartTimeOptions = lastEntry.startTime.options;
        const lastEntryEndDateOptions = lastEntry.endDate.options;
        const lastEntryEndTimeOptions = lastEntry.endTime.options;

        const now = moment().startOf('minute');
        let entryLimit: moment.Duration | undefined;
        const timesheetEndTimeLimit = this.configService.getTimesheetEndTimeLimit();
        // if there is a limit defined then read it from config and set the max value with it
        if (timesheetEndTimeLimit) {
            entryLimit = moment.duration(timesheetEndTimeLimit);
        }
        const nextDayTimesheet = this.getTimesheetForNextDay(firstEntry);

        // SHSNGMAA-553
        // All of the if conditions below, are to make sure the values are only updated once the user put a valid value into the input field.
        // Without this check, the min/maxValues are getting updated even if the value in the input field is invalid.
        if (
            filteredEntries.length === 1 ||
            firstEntry.startDate.value.isSameOrAfter(firstEntry.startDate.options.minValue)
        ) {
            const firstEntryStartDateOptions = firstEntry.startDate.options;
            firstEntryStartDateOptions.minValue.set(firstEntry.startDate.value.clone().startOf('day').toObject());
            firstEntry.startDate = <DateModel>(
                firstEntry.set(
                    new DateModel(firstEntry.startDate.name, firstEntryStartDateTime, firstEntryStartDateOptions),
                )
            );
        }
        if (
            filteredEntries.length === 1 ||
            firstEntry.startTime.value.isSameOrAfter(firstEntry.startTime.options.minValue)
        ) {
            const firstEntryStartTimeOptions = firstEntry.startTime.options;
            firstEntryStartTimeOptions.minValue.set(firstEntry.startTime.value.clone().startOf('day').toObject());
            firstEntry.startTime = <TimeModel>(
                firstEntry.set(
                    new TimeModel(firstEntry.startTime.name, firstEntryStartDateTime, firstEntryStartTimeOptions),
                )
            );
        }

        if (lastEntry.startTime.options.minValue > lastEntry.startTime.options.maxValue) {
            lastEntryStartDateOptions.maxValue.set(lastEntry.endDate.value.clone().toObject());
            lastEntry.startDate = <DateModel>(
                lastEntry.set(
                    new DateModel(lastEntry.startDate.name, lastEntryStartDateTime, lastEntryStartDateOptions),
                )
            );
            lastEntryStartTimeOptions.maxValue.set(lastEntry.endTime.value.clone().toObject());
            lastEntry.startTime = <TimeModel>(
                lastEntry.set(
                    new TimeModel(lastEntry.startTime.name, lastEntryStartDateTime, lastEntryStartTimeOptions),
                )
            );
            lastEntryEndDateOptions.maxValue.set(now.clone().toObject());
            lastEntry.endDate = <DateModel>(
                lastEntry.set(new DateModel(lastEntry.endDate.name, lastEntryEndDateTime, lastEntryEndDateOptions))
            );
            lastEntryEndTimeOptions.maxValue.set(now.clone().toObject());
            lastEntry.endTime = <TimeModel>(
                lastEntry.set(new TimeModel(lastEntry.endTime.name, lastEntryEndDateTime, lastEntryEndTimeOptions))
            );
        }

        if (
            filteredEntries.length === 1 ||
            lastEntry.startDate.value.isSameOrBefore(lastEntry.startDate.options.maxValue)
        ) {
            // if there is no value for end limit from config set it to end of day
            lastEntryStartDateOptions.maxValue.set(
                entryLimit
                    ? nextDayTimesheet
                        ? nextDayTimesheet.startDate.value.clone().toObject()
                        : now.clone().add(entryLimit).toObject()
                    : lastEntry.startDate.value.clone().endOf('day').toObject(),
            );
            lastEntry.startDate = <DateModel>(
                lastEntry.set(
                    new DateModel(lastEntry.startDate.name, lastEntryStartDateTime, lastEntryStartDateOptions),
                )
            );
        }

        if (
            filteredEntries.length === 1 ||
            lastEntry.startTime.value.isSameOrBefore(lastEntry.startTime.options.maxValue)
        ) {
            // if there is no value for end limit from config set it to end of day
            lastEntryStartTimeOptions.maxValue.set(
                entryLimit
                    ? nextDayTimesheet
                        ? nextDayTimesheet.startTime.value.clone().toObject()
                        : now.clone().add(entryLimit).toObject()
                    : lastEntry.startTime.value.clone().endOf('day').toObject(),
            );
            lastEntry.startTime = <TimeModel>(
                lastEntry.set(
                    new TimeModel(lastEntry.startTime.name, lastEntryStartDateTime, lastEntryStartTimeOptions),
                )
            );
        }

        if (
            filteredEntries.length === 1 ||
            lastEntry.endDate.value.isSameOrBefore(lastEntry.endDate.options.maxValue)
        ) {
            // if there is no value for end limit from config set it to end of day
            lastEntryEndDateOptions.maxValue.set(
                entryLimit
                    ? nextDayTimesheet
                        ? nextDayTimesheet.startDate.value.clone().toObject()
                        : now.clone().add(entryLimit).toObject()
                    : lastEntry.endDate.value.clone().endOf('day').toObject(),
            );
            if (entryLimit && lastEntry.startDate.value.isSameOrAfter(lastEntry.startDate.options.minValue)) {
                // set the min value of last entry to the start date
                // if this is final entry in timesheet this needs to be done
                lastEntryEndDateOptions.minValue.set(lastEntry.startDate.value.clone().toObject());
            }
            lastEntry.endDate = <DateModel>(
                lastEntry.set(new DateModel(lastEntry.endDate.name, lastEntryEndDateTime, lastEntryEndDateOptions))
            );
        }
        if (
            filteredEntries.length === 1 ||
            lastEntry.endTime.value.isSameOrBefore(lastEntry.endTime.options.maxValue)
        ) {
            // if there is no value for end limit from config set it to end of day
            lastEntryEndTimeOptions.maxValue.set(
                entryLimit
                    ? nextDayTimesheet
                        ? nextDayTimesheet.startTime.value.clone().toObject()
                        : now.clone().add(entryLimit).toObject()
                    : lastEntry.endTime.value.clone().endOf('day').toObject(),
            );
            if (entryLimit && lastEntry.startTime.value.isSameOrAfter(lastEntry.startTime.options.minValue)) {
                // set the min value of last entry to the start time
                // if this is final entry in timesheet this needs to be done
                lastEntryEndTimeOptions.minValue.set(lastEntry.startTime.value.clone().toObject());
            }
            lastEntry.endTime = <TimeModel>(
                lastEntry.set(new TimeModel(lastEntry.endTime.name, lastEntryEndDateTime, lastEntryEndTimeOptions))
            );
        }
    }

    /**
     * Takes a gap-less list of entries and updates the minValue for the start time and the max value for the end time
     * so that no overlap is allowed to be entered by the user
     *
     * @param {TimesheetEntryInterface[]} entries the list of timesheet entries
     */
    private updateStartAndEndLimits(entries: TimesheetEntryInterface[]): void {
        let previousEntry: TimesheetEntryInterface | undefined;
        const tomorrowEndOfDay = moment().add(1, 'day').endOf('day');

        entries
            .filter((entry) => !TimesheetAndExpensesService.isGapEntry(entry))
            .forEach((entry) => {
                if (previousEntry) {
                    const maxValueTime = entry.startTime.value.isAfter(tomorrowEndOfDay)
                        ? tomorrowEndOfDay.toObject()
                        : entry.startTime.value.toObject();
                    const minValueTime = previousEntry.endTime.value.toObject();

                    // Due to the fact that the date and time are binded to other components,
                    // we need to change the reference for the changeDetection to work for all the date time inputs
                    const previousEntryStartDateTime = previousEntry.startDate.value;
                    let previousEntryEndDateTime = previousEntry.endDate.value;
                    if (TimesheetAndExpensesService.isSowEntry(previousEntry)) {
                        previousEntryEndDateTime = previousEntryStartDateTime;
                    }

                    const previousEntryStartDateOptions = previousEntry.startDate.options;
                    previousEntryStartDateOptions.maxValue.set(maxValueTime);
                    previousEntry.startDate = <DateModel>(
                        previousEntry.set(
                            new DateModel(
                                previousEntry.startDate.name,
                                previousEntryStartDateTime,
                                previousEntryStartDateOptions,
                            ),
                        )
                    );
                    const previousEntryStartTimeOptions = previousEntry.startTime.options;
                    previousEntryStartTimeOptions.maxValue.set(maxValueTime);
                    previousEntry.startTime = <TimeModel>(
                        previousEntry.set(
                            new TimeModel(
                                previousEntry.startTime.name,
                                previousEntryStartDateTime,
                                previousEntryStartTimeOptions,
                            ),
                        )
                    );
                    const previousEntryEndDateOptions = previousEntry.endDate.options;
                    previousEntryEndDateOptions.maxValue.set(maxValueTime);
                    previousEntry.endDate = <DateModel>(
                        previousEntry.set(
                            new DateModel(
                                previousEntry.endDate.name,
                                previousEntryEndDateTime,
                                previousEntryEndDateOptions,
                            ),
                        )
                    );
                    const previousEntryEndTimeOptions = previousEntry.endTime.options;
                    previousEntryEndTimeOptions.maxValue.set(maxValueTime);
                    previousEntry.endTime = <TimeModel>(
                        previousEntry.set(
                            new TimeModel(
                                previousEntry.endTime.name,
                                previousEntryEndDateTime,
                                previousEntryEndTimeOptions,
                            ),
                        )
                    );

                    let entryStartDateTime = entry.startDate.value;
                    const entryEndDateTime = entry.endDate.value;

                    const entryEndDateOptions = entry.endDate.options;
                    entryEndDateOptions.minValue.set(maxValueTime);
                    entry.endDate = <DateModel>(
                        entry.set(new DateModel(entry.endDate.name, entryEndDateTime, entryEndDateOptions))
                    );
                    const entryEndTimeOptions = entry.endTime.options;
                    entryEndTimeOptions.minValue.set(maxValueTime);
                    entry.endTime = <TimeModel>(
                        entry.set(new TimeModel(entry.endTime.name, entryEndDateTime, entryEndTimeOptions))
                    );

                    if (TimesheetAndExpensesService.isEowEntry(entry)) {
                        entryStartDateTime = entryEndDateTime;
                        // For EOW entry, the start and end date/time values are same. So, set the minimum values for both to the previous entry's end date/time values.
                        entryEndDateOptions.minValue.set(minValueTime);
                        entry.endDate = <DateModel>(
                            entry.set(new DateModel(entry.endDate.name, entryEndDateTime, entryEndDateOptions))
                        );
                        entryEndTimeOptions.minValue.set(minValueTime);
                        entry.endTime = <TimeModel>(
                            entry.set(new TimeModel(entry.endTime.name, entryEndDateTime, entryEndTimeOptions))
                        );
                    }

                    const entryStartDateOptions = entry.startDate.options;
                    entryStartDateOptions.minValue.set(minValueTime);
                    // reset the max value. start date <= end date
                    entryStartDateOptions.maxValue.set(entry.endTime.value.toObject());
                    entry.startDate = <DateModel>(
                        entry.set(new DateModel(entry.startDate.name, entryStartDateTime, entryStartDateOptions))
                    );
                    const entryStartTimeOptions = entry.startTime.options;
                    entryStartTimeOptions.minValue.set(minValueTime);
                    // reset the max value. start time <= end time
                    entryStartDateOptions.maxValue.set(entry.endTime.value.toObject());
                    entry.startTime = <TimeModel>(
                        entry.set(new TimeModel(entry.startTime.name, entryStartDateTime, entryStartTimeOptions))
                    );
                }

                // update previous entry
                previousEntry = entry;
            });
    }

    /**
     * Check if there are any invalid manual entries.
     * - Every manual entry needs to have an address set and
     * that address should be present in the local storage
     *
     * @param entries the list of timesheet entries
     * @returns true if all manual entries are valid
     */
    public checkManualEntryAddresses(entries: TimesheetEntryInterface[]): boolean {
        const addresses = this.timesheetAndExpensesService.getAddresses();

        entries.map((entry) => {
            if (TimesheetAndExpensesService.isManualEntry(entry)) {
                const entryAddress = entry.address;

                if (entryAddress && entryAddress.isValid()) {
                    const existingAddress = addresses.find((addr) => {
                        return (
                            addr.addrText.value === entryAddress.addrText.value &&
                            addr.city.value === entryAddress.city.value &&
                            addr.street.value === entryAddress.street.value &&
                            addr.postCode.value === entryAddress.postCode.value &&
                            addr.addrNumber.value === entryAddress.addrNumber.value
                        );
                    });
                    // It is possible that user has just now deleted manually added addresses,
                    // If the manual entry still has such address, it needs to be removed
                    if (!existingAddress) {
                        entry.address = undefined;
                    }
                }
            }
        });

        const invalidManualEntry = entries.find((entry: TimesheetEntryInterface) => {
            let hasInvalidAddress = false;
            if (TimesheetAndExpensesService.isManualEntry(entry)) {
                hasInvalidAddress = !(entry.address && entry.address.isValid());
            }
            return hasInvalidAddress;
        });

        // if there is an invalid entry, we want to return false
        return typeof invalidManualEntry === 'undefined';
    }

    /**
     * Check if there are any unfilled mandatory gap entries left
     *
     * @param entries the list of timesheet entries
     * @returns true if there are no unfilled mandatory gap entries
     */
    public checkForUnfilledGaps(entries: TimesheetEntryInterface[]): boolean {
        return entries.filter((entry) => TimesheetAndExpensesService.isGapEntry(entry) && entry.mandatory).length === 0;
    }

    public isTimesheetInvalidForNormalEditableEntries(
        timeRecordingEntries: TimeRecordingEntryModel[],
    ): Observable<TimesheetNormalEditableEntriesValidationResult> {
        let result = new TimesheetNormalEditableEntriesValidationResult();

        const timesheet = this.timesheetAndExpensesService.getCurrentTimesheet();
        // get first travel to entry of timesheet which is editable
        const firstEditableTravelToEntry = timesheet
            .getTimesheetEntries()
            .find(
                (entry) =>
                    TimesheetAndExpensesService.isNormalEntry(entry) &&
                    TimesheetAndExpensesService.isTravelToEntry(entry) &&
                    entry.isTravelToEditable,
            );
        // check if the current time recording is before the firstEditableTravelToEntry
        if (firstEditableTravelToEntry) {
            const firstEntryOfTimeRecording = timeRecordingEntries[0];
            if (
                firstEntryOfTimeRecording.endDateTime.isSameOrBefore(firstEditableTravelToEntry.endDate.value) &&
                firstEditableTravelToEntry.startDate.value.isAfter(firstEditableTravelToEntry.endDate.value)
            ) {
                result = new TimesheetNormalEditableEntriesValidationResult(
                    true,
                    (<TimesheetEntryModel>firstEditableTravelToEntry).notificationId,
                    `Timesheet's first travelTo entry start date is invalid`,
                );
            }
        }
        // get last travel back entry of timesheet which is editable
        const lastEditableTravelBackEntry = timesheet
            .getTimesheetEntries()
            .find(
                (entry) =>
                    TimesheetAndExpensesService.isNormalEntry(entry) &&
                    TimesheetAndExpensesService.isTravelBackEntry(entry) &&
                    entry.isTravelBackEditable,
            );
        // check if the current time recording is after the lastEditableTravelBackEntry
        if (lastEditableTravelBackEntry) {
            const lastEntryOfTimeRecording = timeRecordingEntries[timeRecordingEntries.length - 1];
            if (
                lastEntryOfTimeRecording.startDateTime.isSameOrAfter(lastEditableTravelBackEntry.startDate.value) &&
                lastEditableTravelBackEntry.endDate.value.isBefore(lastEditableTravelBackEntry.startDate.value)
            ) {
                result = new TimesheetNormalEditableEntriesValidationResult(
                    true,
                    (<TimesheetEntryModel>lastEditableTravelBackEntry).notificationId,
                    `Timesheet's last travelBack entry end date is invalid`,
                );
            }
        }
        this.log.debug(
            `TimesheetService: isTimesheetInvalidForNormalEditableEntries: ${result.isInvalid} ${
                result.isInvalid ? `for notification ${result.notificationId} ${result.validationErrorMessage}` : ''
            }`,
        );

        return of(result);
    }
}
